import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useNavigate } from "react-router-dom";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { useAuth } from "../../../core/auth";
import { notify } from "../../../utils/toast";
import { track } from "@amplitude/analytics-browser";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// 배너 비율 상수: 1024:182 (width:height)
const BANNER_ASPECT_RATIO = 182 / 1024; // 약 0.1777 (17.77%)

const GET_BANNERS = gql(`
  query GetBanners($address: String) {
    banners(address: $address) {
      id
      imageUrl
      link
      address
      alt
      needLogin
      isActive
      sortOrder
    }
  }
`);

interface BannerItem {
  id: number;
  imageUrl: string;
  link: string;
  address: string;
  alt: string;
  needLogin?: boolean | null;
  isActive: boolean;
  sortOrder: number;
}

const BannerTop: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const loadAddr = localStorage.getItem("loadAddr") || "";

  const { data, loading } = useQuery(GET_BANNERS, {
    variables: {
      address: loadAddr,
    },
  });

  // 서버에서 받은 배너 데이터를 필터링하고 정렬
  // 문서에 따르면 서버에서 isActive=false인 배너는 제외되지만, 안전을 위해 클라이언트에서도 필터링
  const banners: BannerItem[] = data?.banners
    ? data.banners
        .filter((banner: BannerItem) => banner.isActive)
        .sort((a: BannerItem, b: BannerItem) => a.sortOrder - b.sortOrder)
    : [];

  const handleBannerClick = (link: string, alt: string) => {
    if (link.startsWith("http")) {
      window.location.href = link; // 외부 링크
    } else {
      track("홈 배너 클릭", { link, alt });
      navigate(link); // 내부 라우팅
    }
  };

  // 주소 기반 필터링 (서버에서 이미 필터링되었을 수 있지만, 클라이언트에서도 확인)
  const filteredBanners = banners.filter((banner) => {
    // 주소 필터링
    if (banner.address === "all") return true;
    if (!loadAddr) return false;
    return loadAddr.includes(banner.address);
  });

  // 로딩 중일 때 스켈레톤 표시
  if (loading) {
    return (
      <div
        className="w-full bg-transparent px-4"
        style={{
          aspectRatio: `${1 / BANNER_ASPECT_RATIO}`, // 1024/182
        }}
      >
        <div className="max-w-screen-2xl mx-auto h-full">
          <div className="rounded-2xl overflow-hidden border h-full">
            <div className="skeleton-wave-item w-full h-full">
              <Skeleton width="100%" height="100%" borderRadius="16px" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full bg-transparent px-4"
      style={{
        aspectRatio: `${1 / BANNER_ASPECT_RATIO}`, // 1024/182
      }}
    >
      <div className="max-w-screen-2xl mx-auto h-full">
        <div className="rounded-2xl overflow-hidden border h-full">
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            autoplay={{ delay: 3000 }}
            pagination={{
              clickable: true,
            }}
            className="rounded [&_.swiper-pagination]:!bottom-[-4px] [&_.swiper-pagination]:!mb-0 h-full"
          >
            {filteredBanners.map((banner) => {
              return (
                <SwiperSlide key={banner.id} className="h-full">
                  <div
                    className="relative w-full h-full cursor-pointer"
                    onClick={() => {
                      if (banner.needLogin === true && !isLoggedIn) {
                        notify("로그인이 필요한 페이지에요!");
                      }
                      handleBannerClick(banner.link, banner.alt);
                    }}
                  >
                    <ResponsiveCDNImage
                      src={banner.imageUrl}
                      alt={banner.alt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0" />
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </div>
  );
};

export default BannerTop;
